// ============================================================================
// PulseHistory — historique des pulses d'une equipe (E1b, F-221/F-240)
// ============================================================================
// Portage du PoC components/equipes/pulse-history.tsx : grille de cellules
// teintees par score (agrege mood_score, visible a toute la gestion) + detail
// NARRATIF du plus recent (challenges/positives/support_needed) UNIQUEMENT si le
// viewer y a droit (M-208, admin OU directeur — PII orange). Le serveur ne
// selectionne meme pas ces colonnes sinon (ceinture + bretelles) ; ce composant
// affiche le message de restriction quand canSeeDetail est faux.
// Teintes = echelles nommees semantiques (green/amber/red — comme StatusBadge),
// jamais de hex (regle #28). Presentationnel.

import { useTranslation } from '../../i18n';
import { formatPeriodLong, monthShort } from '../../pages/equipes/dates';
import type { TeamPulse } from '../../hooks/useTeams';

/** Teinte semantique par score : 4-5 sain, 3 a surveiller, 1-2 preoccupant. */
function moodClasses(score: number): string {
  if (score >= 4) return 'border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-950/40 dark:text-green-400';
  if (score === 3) return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-400';
  return 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-400';
}

function DetailField({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-1">
      <p className="text-chip font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="whitespace-pre-wrap text-field text-slate-700 dark:text-slate-300">{value}</p>
    </div>
  );
}

export function PulseHistory({ pulses, canSeeDetail }: { pulses: TeamPulse[]; canSeeDetail: boolean }) {
  const { t, i18n } = useTranslation();
  const chrono = pulses.slice().reverse(); // serveur = plus recent d'abord -> chrono
  const latest = pulses[0] ?? null;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6 lg:grid-cols-12">
        {chrono.map((p) => (
          <div
            key={p.id}
            title={t('gest:equipes.pulse.cell', { period: formatPeriodLong(i18n.language, p.period), score: p.moodScore })}
            className={`flex w-full flex-col items-center gap-0.5 rounded-lg border p-2 ${moodClasses(p.moodScore)}`}
          >
            <span className="text-chip font-medium uppercase tracking-wide opacity-70">
              {monthShort(i18n.language, p.period)}
            </span>
            <span className="text-body font-semibold tabular-nums">{p.moodScore}</span>
          </div>
        ))}
      </div>

      {latest && canSeeDetail ? (
        <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <header className="mb-3 flex items-baseline justify-between gap-3">
            <div>
              <p className="text-chip font-semibold uppercase tracking-wide text-slate-400">
                {t('gest:equipes.pulse.latest')}
              </p>
              <h4 className="mt-0.5 text-field font-semibold text-slate-900 dark:text-slate-100">
                {formatPeriodLong(i18n.language, latest.period)}
              </h4>
            </div>
            <span className="text-head font-bold tabular-nums text-slate-800 dark:text-slate-100">
              {t('gest:equipes.pulse.score', { score: latest.moodScore })}
            </span>
          </header>
          <div className="flex flex-col gap-3">
            <DetailField label={t('gest:equipes.pulse.challenges')} value={latest.challenges} />
            <DetailField label={t('gest:equipes.pulse.positives')} value={latest.positives} />
            <DetailField label={t('gest:equipes.pulse.support')} value={latest.supportNeeded} />
          </div>
        </div>
      ) : null}

      {latest && !canSeeDetail ? (
        <p className="text-meta text-slate-500 dark:text-slate-400">{t('gest:equipes.pulse.detailRestricted')}</p>
      ) : null}
    </div>
  );
}
